import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import "./styles/global.css";
import "./lib/i18n";
import { AuthProvider } from "./lib/auth-provider";
import { ToastProvider } from "./lib/toast-provider";
import { AppTitle } from "./components/AppTitle";
import { rootRoute } from "./routes/__root";
import { route as indexRoute } from "./routes/index";
import { route as loginRoute } from "./routes/login";
import { route as accountRoute } from "./routes/account";
import { route as syncRoute } from "./routes/sync";
import { route as chatRoute } from "./routes/chat";
import { route as adminSettingsRoute } from "./routes/admin.settings";
import { route as adminLogsRoute } from "./routes/admin.logs";
import { route as adminUsersRoute } from "./routes/admin.users";
import { route as adminEngineBrandsRoute } from "./routes/admin.engine-brands";
import { route as companiesRoute } from "./routes/companies";
import { route as companyDetailRoute } from "./routes/companies.$id";
import { route as leadsRoute } from "./routes/leads";
import { route as leadDetailRoute } from "./routes/leads.$id";
import { route as activitiesRoute } from "./routes/activities";
import { route as activityDetailRoute } from "./routes/activities.$id";
import { route as offersRoute } from "./routes/offers";
import { route as offerDetailRoute } from "./routes/offers.$id";
import { route as offerNewRoute } from "./routes/offers.new";
import { route as offerEditRoute } from "./routes/offers.$id.edit";
import { route as ordersRoute } from "./routes/orders";
import { route as orderDetailRoute } from "./routes/orders.$id";
import { route as orderNewRoute } from "./routes/orders.new";
import { route as orderEditRoute } from "./routes/orders.$id.edit";
import { route as contractsRoute } from "./routes/contracts";
import { route as contractDetailRoute } from "./routes/contracts.$id";
import { route as contractNewRoute } from "./routes/contracts.new";
import { route as warehousesRoute } from "./routes/warehouses";
import { route as warehouseDetailRoute } from "./routes/warehouses.$id";
import { route as stockItemsRoute } from "./routes/stock-items";
import { route as stockItemDetailRoute } from "./routes/stock-items.$id";
import { route as loadReportsRoute } from "./routes/load-reports";
import { route as deliveriesRoute } from "./routes/deliveries";
import {
	route as priceListRoute,
	exportRoute as priceListExportRoute,
	portableRoute as priceListPortableRoute,
	portableExportRoute as priceListPortableExportRoute,
} from "./routes/price-list";
import { route as reportsRoute } from "./routes/reports";
import { route as mailRoute } from "./routes/mail";
import { route as specialDaysRoute } from "./routes/special-days";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 60 * 1000,
			retry: 1,
		},
	},
});

const routeTree = rootRoute.addChildren([
	indexRoute,
	loginRoute,
	accountRoute,
	syncRoute,
	chatRoute,
	mailRoute,
	adminSettingsRoute,
	adminLogsRoute,
	adminUsersRoute,
	adminEngineBrandsRoute,
	reportsRoute,
	companiesRoute,
	companyDetailRoute,
	leadsRoute,
	leadDetailRoute,
	activitiesRoute,
	activityDetailRoute,
	offersRoute,
	offerDetailRoute,
	offerNewRoute,
	offerEditRoute,
	ordersRoute,
	orderDetailRoute,
	orderNewRoute,
	orderEditRoute,
	contractsRoute,
	contractDetailRoute,
	contractNewRoute,
	warehousesRoute,
	warehouseDetailRoute,
	stockItemsRoute,
	stockItemDetailRoute,
	loadReportsRoute,
	deliveriesRoute,
	priceListRoute,
	priceListExportRoute,
	priceListPortableRoute,
	priceListPortableExportRoute,
	specialDaysRoute,
]);

const router = createRouter({
	routeTree,
	context: { queryClient },
	defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
			<ToastProvider>
				<AuthProvider>
					<AppTitle />
					<RouterProvider router={router} />
				</AuthProvider>
			</ToastProvider>
		</QueryClientProvider>
	</StrictMode>,
);
